import React, { useEffect, useState } from 'react';
import { PlusIcon, EditIcon, TrashIcon, UserIcon, ArchiveIcon, BanIcon, CheckCircleIcon, SearchIcon, FilterIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Modal } from '../ui/Modal';
import { Table } from '../ui/Table';
import { Badge } from '../ui/Badge';
import { User, UserRole } from '../../types';
import { supabase } from '../../utils/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { getRoleName, generateId } from '../../utils/helpers';
type UserStatus = 'active' | 'inactive' | 'archived';
interface UserWithStatus extends User {
  status: UserStatus;
  gender?: string;
}
export function UserManagement() {
  const {
    isConnectedToSupabase
  } = useAuth();
  const {
    roles
  } = useData();
  const [users, setUsers] = useState<UserWithStatus[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserWithStatus[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserWithStatus | null>(null);
  // Filter states
  const [statusFilter, setStatusFilter] = useState<UserStatus | 'all'>('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'administrador' as UserRole,
    gender: '' as string
  });
  const [saveMessage, setSaveMessage] = useState('');
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  // Build role options
  const roleOptions = [{
    value: 'administrador',
    label: 'Administrador'
  }, ...roles.map(r => ({
    value: r.name,
    label: r.displayName || r.name
  }))];

  // Load user statuses from localStorage
  const getUserStatuses = (): Record<string, UserStatus> => {
    try {
      const saved = localStorage.getItem('savidna-user-statuses');
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      return {};
    }
  };
  const saveUserStatus = (userId: string, status: UserStatus) => {
    const statuses = getUserStatuses();
    statuses[userId] = status;
    localStorage.setItem('savidna-user-statuses', JSON.stringify(statuses));
  };
  // Load users from Supabase
  const loadUsers = async () => {
    setIsLoadingUsers(true);
    const statuses = getUserStatuses();
    if (isConnectedToSupabase) {
      try {
        const {
          data,
          error
        } = await supabase.from('users').select('*').order('created_at', {
          ascending: false
        });
        if (data && !error) {
          const mappedUsers: UserWithStatus[] = data.map(u => ({
            id: u.id,
            email: u.email,
            password: u.password || '',
            name: u.name,
            role: u.role as UserRole,
            gender: u.gender || '',
            avatar: u.avatar,
            language: u.language || 'es',
            timezone: u.timezone || 'America/Santo_Domingo',
            createdAt: new Date(u.created_at),
            // FIXED: Prioritize Supabase status over localStorage
            status: u.status as UserStatus || statuses[u.id] || 'active'
          }));
          setUsers(mappedUsers);
          // Sync localStorage with Supabase status
          const updatedStatuses = {
            ...statuses
          };
          mappedUsers.forEach(user => {
            if (user.status !== 'active') {
              updatedStatuses[user.id] = user.status;
            }
          });
          localStorage.setItem('savidna-user-statuses', JSON.stringify(updatedStatuses));
        }
      } catch (e) {
        console.error('Error cargando usuarios:', e);
      }
    } else {
      // Fallback for local development if needed, though usually empty initially
      // For now we rely on the auth context user or empty array
      setUsers([]);
    }
    setIsLoadingUsers(false);
  };
  useEffect(() => {
    loadUsers();
  }, [isConnectedToSupabase]);
  // Filter logic
  useEffect(() => {
    let result = users;
    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter(u => u.status === statusFilter);
    }
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(u => u.name.toLowerCase().includes(term) || u.email.toLowerCase().includes(term) || getRoleName(u.role).toLowerCase().includes(term));
    }
    setFilteredUsers(result);
  }, [users, statusFilter, searchTerm]);
  const handleOpenModal = (user?: UserWithStatus) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        role: user.role,
        gender: user.gender || ''
      });
    } else {
      setEditingUser(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'administrador',
        gender: ''
      });
    }
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'administrador',
      gender: ''
    });
  };
  const handleSaveUser = async () => {
    if (!formData.name || !formData.email) {
      alert('Por favor completa todos los campos requeridos');
      return;
    }
    if (!editingUser && !formData.password) {
      alert('La contraseña es requerida para nuevos usuarios');
      return;
    }
    setIsSaving(true);
    if (isConnectedToSupabase) {
      try {
        if (editingUser) {
          // Update in Supabase
          const updateData: any = {
            name: formData.name,
            email: formData.email.toLowerCase().trim(),
            role: formData.role,
            gender: formData.gender || null
          };
          if (formData.password) {
            updateData.password = formData.password;
          }
          const {
            error
          } = await supabase.from('users').update(updateData).eq('id', editingUser.id);
          if (error) {
            setIsSaving(false);
            alert('Error actualizando usuario: ' + error.message);
            return;
          }
          // Update local state immediately (optimistic)
          setUsers(prev => prev.map(u => u.id === editingUser.id ? {
            ...u,
            name: formData.name,
            email: formData.email.toLowerCase().trim(),
            role: formData.role as UserRole,
            gender: formData.gender || ''
          } : u));
          setSaveMessage('✅ Usuario actualizado');
        } else {
          // Create in Supabase
          const newId = crypto.randomUUID();
          const {
            error
          } = await supabase.from('users').insert({
            id: newId,
            email: formData.email.toLowerCase().trim(),
            password: formData.password,
            name: formData.name,
            role: formData.role,
            gender: formData.gender || null,
            language: 'es',
            timezone: 'America/Santo_Domingo'
          });
          if (error) {
            setIsSaving(false);
            alert('Error creando usuario: ' + error.message);
            return;
          }
          // Add to local state immediately
          const statuses = getUserStatuses();
          setUsers(prev => [{
            id: newId,
            name: formData.name,
            email: formData.email.toLowerCase().trim(),
            password: '',
            role: formData.role as UserRole,
            gender: formData.gender || '',
            language: 'es',
            timezone: 'America/Santo_Domingo',
            createdAt: new Date(),
            status: (statuses[newId] || 'active') as UserStatus
          }, ...prev]);
          setSaveMessage('✅ Usuario creado');
        }
      } catch (e: any) {
        setIsSaving(false);
        alert('Error: ' + (e.message || 'Error desconocido'));
        return;
      }
    } else {
      setIsSaving(false);
      alert('Se requiere conexión a Supabase para gestionar usuarios');
      return;
    }
    setIsSaving(false);
    handleCloseModal();
    setTimeout(() => setSaveMessage(''), 3000);
  };
  const handleStatusChange = (userId: string, newStatus: UserStatus) => {
    saveUserStatus(userId, newStatus);
    setUsers(prev => prev.map(u => u.id === userId ? {
      ...u,
      status: newStatus
    } : u));
    // Persist to Supabase
    supabase.from('users').update({
      status: newStatus
    }).eq('id', userId).then(({
      error
    }) => {
      if (error) console.error('Error updating user status in Supabase:', error);else console.log('✅ User status updated in Supabase:', newStatus);
    });
    const actionName = newStatus === 'active' ? 'activado' : newStatus === 'inactive' ? 'desactivado' : 'archivado';
    setSaveMessage(`Usuario ${actionName} correctamente`);
    setTimeout(() => setSaveMessage(''), 3000);
  };
  const getRoleBadgeVariant = (role: UserRole) => {
    if (role === 'administrador') return 'primary';
    return 'info';
  };
  const getStatusBadgeVariant = (status: UserStatus) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'warning';
      case 'archived':
        return 'default';
    }
  };
  const getStatusLabel = (status: UserStatus) => {
    switch (status) {
      case 'active':
        return 'Activo';
      case 'inactive':
        return 'Inactivo';
      case 'archived':
        return 'Archivado';
    }
  };
  const columns = [{
    key: 'name',
    header: 'Usuario',
    render: (user: UserWithStatus) => <div className="flex items-center gap-3" data-id="element-4360">
          {user.avatar ? <img src={user.avatar} alt={user.name} className={`w-10 h-10 rounded-full object-cover border-2 ${user.status === 'active' ? 'border-green-400' : 'border-gray-200 dark:border-gray-600'}`} data-id="element-4361" /> : <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${user.status === 'active' ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'}`} data-id="element-4362">
        
              {user.name.charAt(0).toUpperCase()}
            </div>}
          <div data-id="element-4363">
            <div className={`font-medium ${user.status === 'archived' ? 'text-gray-500 line-through' : 'text-gray-900 dark:text-white'}`} data-id="element-4364">
          
              {user.name}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4365">
              {user.email}
            </div>
          </div>
        </div>
  }, {
    key: 'role',
    header: 'Rol',
    render: (user: UserWithStatus) => <Badge variant={getRoleBadgeVariant(user.role)} data-id="element-4366">
          {getRoleName(user.role)}
        </Badge>
  }, {
    key: 'status',
    header: 'Estado',
    render: (user: UserWithStatus) => <Badge variant={getStatusBadgeVariant(user.status)} data-id="element-4367">
          {getStatusLabel(user.status)}
        </Badge>
  }, {
    key: 'actions',
    header: 'Acciones',
    render: (user: UserWithStatus) => <div className="flex items-center gap-1" data-id="element-4368">
          <button onClick={() => handleOpenModal(user)} className="p-1.5 text-gray-500 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-lg transition-colors" title="Editar usuario" data-id="element-4369">
        
            <EditIcon className="w-4 h-4" data-id="element-4370" />
          </button>

          {user.status === 'active' ? <button onClick={() => handleStatusChange(user.id, 'inactive')} className="p-1.5 text-gray-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors" title="Desactivar usuario" data-id="element-4371">
        
              <BanIcon className="w-4 h-4" data-id="element-4372" />
            </button> : <button onClick={() => handleStatusChange(user.id, 'active')} className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors" title="Activar usuario" data-id="element-4373">
        
              <CheckCircleIcon className="w-4 h-4" data-id="element-4374" />
            </button>}

          {user.status !== 'archived' ? <button onClick={() => handleStatusChange(user.id, 'archived')} className="p-1.5 text-gray-500 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors" title="Archivar usuario" data-id="element-4375">
        
              <ArchiveIcon className="w-4 h-4" data-id="element-4376" />
            </button> : <button onClick={() => handleStatusChange(user.id, 'active')} className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors" title="Restaurar usuario" data-id="element-4377">
        
              <PlusIcon className="w-4 h-4" data-id="element-4378" />
            </button>}
        </div>
  }];
  return <div className="space-y-6" data-id="element-4379">
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-4380">
        <div data-id="element-4381">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-4382">
            <UserIcon className="w-6 h-6 text-teal-600 dark:text-teal-400" data-id="element-4383" />
            Gestión de Usuarios
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-4384">
            {users.length} usuarios registrados en el sistema
          </p>
        </div>
        <Button onClick={() => handleOpenModal()} data-id="element-4385">
          <PlusIcon className="w-4 h-4" data-id="element-4386" />
          Nuevo Usuario
        </Button>
      </div>

      {saveMessage && <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-600 dark:text-green-400 text-sm animate-in fade-in slide-in-from-top-2" data-id="element-4387">
          {saveMessage}
        </div>}

      {/* Filters */}
      <Card className="p-4" data-id="element-4388">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between" data-id="element-4389">
          <div className="flex bg-gray-100 dark:bg-gray-800 p-1 rounded-lg w-full md:w-auto" data-id="element-4390">
            {(['active', 'inactive', 'archived', 'all'] as const).map(status => <button key={status} onClick={() => setStatusFilter(status)} className={`
                  px-4 py-1.5 text-sm font-medium rounded-md transition-all flex-1 md:flex-none capitalize
                  ${statusFilter === status ? 'bg-white dark:bg-gray-700 text-teal-600 dark:text-teal-400 shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}
                `} data-id="element-4391">
                
                  {status === 'all' ? 'Todos' : getStatusLabel(status)}
                </button>)}
          </div>

          <div className="relative w-full md:w-64" data-id="element-4392">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-4393" />
            <input type="text" placeholder="Buscar usuarios..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all" data-id="element-4394" />
            
          </div>
        </div>
      </Card>

      {/* Users List */}
      <Card className="overflow-hidden" data-id="element-4395">
        {isLoadingUsers ? <div className="p-12 text-center text-gray-500" data-id="element-4396">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500 mx-auto mb-3" data-id="element-4397"></div>
            Cargando usuarios...
          </div> : filteredUsers.length === 0 ? <div className="py-12 text-center text-gray-500 dark:text-gray-400 text-sm" data-id="element-4398">No se encontraron usuarios con los filtros seleccionados</div> : <>
          {/* Mobile cards */}
          <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-800" data-id="element-4399">
            {filteredUsers.map(user => <div key={user.id} className="px-4 py-3 flex items-center gap-3" data-id="element-4400">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold flex-shrink-0 ${user.status === 'active' ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'}`} data-id="element-4401">
                  {user.avatar ? <img src={user.avatar} alt={user.name} className="w-10 h-10 rounded-full object-cover" data-id="element-4402" /> : user.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0" data-id="element-4403">
                  <p className={`font-semibold text-sm truncate ${user.status === 'archived' ? 'text-gray-400 line-through' : 'text-gray-900 dark:text-white'}`} data-id="element-4404">{user.name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate" data-id="element-4405">{user.email}</p>
                  <div className="flex items-center gap-2 mt-1" data-id="element-4406">
                    <Badge variant={getRoleBadgeVariant(user.role)} className="text-xs py-0 px-1.5" data-id="element-4407">{getRoleName(user.role)}</Badge>
                    <Badge variant={getStatusBadgeVariant(user.status)} className="text-xs py-0 px-1.5" data-id="element-4408">{getStatusLabel(user.status)}</Badge>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0" data-id="element-4409">
                  <button onClick={() => handleOpenModal(user)} className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-lg transition-colors" title="Editar" data-id="element-4410"><EditIcon className="w-4 h-4" data-id="element-4411" /></button>
                  {user.status === 'active' ? <button onClick={() => handleStatusChange(user.id, 'inactive')} className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors" title="Desactivar" data-id="element-4412"><BanIcon className="w-4 h-4" data-id="element-4413" /></button> : <button onClick={() => handleStatusChange(user.id, 'active')} className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors" title="Activar" data-id="element-4414"><CheckCircleIcon className="w-4 h-4" data-id="element-4415" /></button>}
                </div>
              </div>)}
          </div>
          {/* Desktop table */}
          <div className="hidden sm:block" data-id="element-4416">
            <Table columns={columns} data={filteredUsers} keyExtractor={user => user.id} emptyMessage="No se encontraron usuarios" data-id="element-4417" />
          </div>
        </>}
      </Card>

      {/* User Modal */}
      <Modal isOpen={isModalOpen} onClose={handleCloseModal} title={editingUser ? 'Editar Usuario' : 'Nuevo Usuario'} size="md" icon={<UserIcon className="w-5 h-5" data-id="element-4419" />} accentColor="sky" data-id="element-4418">
        
        <div className="space-y-6" data-id="element-4420">
          {/* Personal Info Section */}
          <div data-id="element-4421">
            <div className="flex items-center gap-2 mb-4" data-id="element-4422">
              <span className="w-1.5 h-5 rounded-full bg-teal-500" data-id="element-4423"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-4424">
                Información Personal
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-4425">
              <Input label="Nombre Completo" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} placeholder="Ej: Juan Pérez" data-id="element-4426" />
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-4427">
                <Input label="Correo Electrónico" type="email" value={formData.email} onChange={e => setFormData({
                ...formData,
                email: e.target.value
              })} placeholder="usuario@ejemplo.com" data-id="element-4428" />
                
                <Select label="Género" options={[{
                value: 'masculino',
                label: 'Masculino'
              }, {
                value: 'femenino',
                label: 'Femenino'
              }, {
                value: 'otro',
                label: 'Otro'
              }]} value={formData.gender} onChange={value => setFormData({
                ...formData,
                gender: value
              })} data-id="element-4429" />
                
              </div>
            </div>
          </div>

          {/* Access & Security Section */}
          <div data-id="element-4430">
            <div className="flex items-center gap-2 mb-4" data-id="element-4431">
              <span className="w-1.5 h-5 rounded-full bg-amber-500" data-id="element-4432"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-4433">
                Acceso y Seguridad
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-4434">
              <Input label={editingUser ? 'Nueva Contraseña (opcional)' : 'Contraseña'} type="password" value={formData.password} onChange={e => setFormData({
              ...formData,
              password: e.target.value
            })} placeholder="••••••••" data-id="element-4435" />
              
              <Select label="Rol del Usuario" options={roleOptions} value={formData.role} onChange={value => setFormData({
              ...formData,
              role: value as UserRole
            })} data-id="element-4436" />
              
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700" data-id="element-4437">
            <Button variant="secondary" onClick={handleCloseModal} disabled={isSaving} data-id="element-4438">
              
              Cancelar
            </Button>
            <Button onClick={handleSaveUser} isLoading={isSaving} data-id="element-4439">
              {editingUser ? 'Actualizar' : 'Crear'} Usuario
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}